import { Component, inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import {
  FormControl,
  FormGroup,
  Validators,
  AbstractControl,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { finalize, firstValueFrom } from 'rxjs';
import { CustomerService } from '../../services/customer/customer.service';
import { CreditPlan } from '../../model/CreditPlan';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Customer } from 'src/app/model/Customer';
import { UserService } from 'src/app/services/user/user.service';
import {
  CreateTransactionRequestData,
  CreateTransactionRequestPlanOffer,
  TransactionRequestService,
} from 'src/app/services/transaction-request/transaction-request.service';
import { FileService } from 'src/app/services/file/file.service';
import {
  clampPercentInput,
  computePlanTotals,
  formatPlanAmount,
  resolveBuyerShare,
  SelectedPlanOffer,
  toSelectedOffer,
} from 'src/app/shared/plan-pricing';

/** Dialog data provided to Build Offer Dialog */
export interface BuildOfferDialogData {
  customer: Customer;
}

/** Overlay step for Review & send flow */
export type BuildOfferOverlayStep = 'form' | 'review' | 'sent' | 'error';

/** Row model for plan card UI */
export interface PlanRowView {
  plan: CreditPlan;
  on: boolean;
  over: boolean;
  totalStr: string;
  feeStr: string;
  mdrPct: string;
  showOverride: boolean;
  custom: boolean;
  overrideVal: string;
  overrideLabel: string;
  needsSplit: boolean;
}

@Component({
  selector: 'app-build-offer-dialog',
  templateUrl: './build-offer-dialog.component.html',
  styleUrl: './build-offer-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class BuildOfferDialogComponent implements OnInit {
  private readonly _dialogRef: MatDialogRef<BuildOfferDialogComponent> =
    inject(MatDialogRef);
  private readonly _customerService: CustomerService = inject(CustomerService);
  private readonly _transactionRequestService: TransactionRequestService =
    inject(TransactionRequestService);
  private readonly _toastrService: ToastrService = inject(ToastrService);
  private readonly _userService: UserService = inject(UserService);
  private readonly _fileService: FileService = inject(FileService);

  public data: BuildOfferDialogData = inject(MAT_DIALOG_DATA);

  submitForm: FormGroup;
  creditPlans: CreditPlan[] = [];
  selectedPlanIds = new Set<number>();
  overrides: Record<number, string> = {};
  isSubmitting = false;
  uploadedFiles: File[] = [];
  isLoadingCreditPlans = true;
  showMdrTip = false;
  overlayStep: BuildOfferOverlayStep = 'form';
  errorTime = '';

  private _supplierId!: string;

  constructor() {
    this.submitForm = new FormGroup({
      amount: new FormControl('', [
        Validators.required,
        Validators.min(0.01),
        Validators.max(this.data.customer.availableBalance),
        Validators.pattern(/^\d+(\.\d{1,2})?$/),
      ]),
      grossUp: new FormControl(false),
      splitPct: new FormControl('0'),
      ponumber: new FormControl(''),
      inumber: new FormControl(''),
      invoice: new FormControl<File[] | null>(null, [this.invoiceValidator()]),
      memo: new FormControl(''),
    });
  }

  ngOnInit(): void {
    this.creditPlans = [];
    this.selectedPlanIds.clear();
    this.overrides = {};

    const companyId = '' + this._userService.company?.companyId;
    if (!companyId) {
      throw new Error('Company ID is undefined');
    }
    this._supplierId = companyId;

    this._customerService
      .getCreditPlans$(this._supplierId)
      .pipe(finalize(() => (this.isLoadingCreditPlans = false)))
      .subscribe({
        next: (plans: CreditPlan[]) => {
          this.creditPlans = plans.filter((p) => p.active !== false);
        },
        error: (err: unknown) => {
          console.error(
            '[BUILD-OFFER-DIALOG-COMPONENT]: Error fetching credit plans:',
            err,
          );
        },
      });
  }

  get desiredNet(): number {
    const raw = this.submitForm.get('amount')?.value;
    const n = parseFloat(String(raw).replace(/[^0-9.]/g, ''));
    return isNaN(n) ? 0 : n;
  }

  get grossUp(): boolean {
    return !!this.submitForm.get('grossUp')?.value;
  }

  get splitPct(): string {
    return String(this.submitForm.get('splitPct')?.value ?? '');
  }

  get availableCredit(): number {
    return this.data.customer.availableBalance;
  }

  get creditLimit(): number {
    return this.data.customer.accountLimit;
  }

  get splitEmpty(): boolean {
    return this.grossUp && this.splitPct.trim() === '';
  }

  get sponsorStr(): string {
    if (!this.grossUp || this.splitEmpty) {
      return '';
    }
    const buyer = Math.min(100, Math.max(0, parseFloat(this.splitPct) || 0));
    return `You sponsor ${Math.max(0, 100 - buyer).toFixed(0)}% of each plan’s MDR`;
  }

  get selectedCount(): number {
    return this.selectedPlanIds.size;
  }

  get countLabel(): string {
    const n = this.selectedCount;
    return n === 1 ? '1 plan selected' : `${n} plans selected`;
  }

  get selectedOffers(): SelectedPlanOffer[] {
    const offers: SelectedPlanOffer[] = [];
    for (const plan of this.creditPlans) {
      if (!this.selectedPlanIds.has(plan.id)) {
        continue;
      }
      const hasOverride = this.grossUp && this.overrides[plan.id] !== undefined;
      const share = resolveBuyerShare(
        this.grossUp,
        this.splitPct,
        hasOverride ? this.overrides[plan.id] : undefined,
      );
      const computed = computePlanTotals(
        this.desiredNet,
        plan.mdr,
        share,
        this.grossUp,
      );
      const splitDisplay =
        share === null ? null : this.grossUp ? Math.round(share * 100) : null;
      const offer = toSelectedOffer(plan, computed, splitDisplay, hasOverride);
      if (offer) {
        offers.push(offer);
      }
    }
    return offers;
  }

  get anyOver(): boolean {
    return this.planRows.some((r) => r.over);
  }

  get needSplit(): boolean {
    return (
      this.grossUp &&
      this.selectedCount > 0 &&
      this.desiredNet > 0 &&
      this.selectedOffers.length < this.selectedCount
    );
  }

  get canSubmit(): boolean {
    return (
      this.desiredNet > 0 &&
      this.selectedCount >= 1 &&
      this.selectedOffers.length === this.selectedCount &&
      !this.anyOver
    );
  }

  get overMsg(): string {
    return (
      `A selected plan’s total exceeds the customer’s ` +
      `${formatPlanAmount(this.availableCredit)} available credit. Lower the desired net or adjust its fee split.`
    );
  }

  get summaryRangeStr(): string {
    const totals = this.selectedOffers.map((o) => o.total);
    if (!totals.length) {
      return '—';
    }
    const lo = Math.min(...totals);
    const hi = Math.max(...totals);
    if (hi - lo < 0.005) {
      return formatPlanAmount(lo);
    }
    return `${formatPlanAmount(lo)} – ${formatPlanAmount(hi)}`;
  }

  get summaryCountStr(): string {
    const n = this.selectedOffers.length;
    return n === 1 ? '1 plan' : `${n} plans`;
  }

  get summaryNetStr(): string {
    return this.desiredNet > 0 ? formatPlanAmount(this.desiredNet) : '—';
  }

  get planRows(): PlanRowView[] {
    return this.creditPlans.map((plan) => {
      const on = this.selectedPlanIds.has(plan.id);
      const hasOverride = this.grossUp && this.overrides[plan.id] !== undefined;
      const share = resolveBuyerShare(
        this.grossUp,
        this.splitPct,
        hasOverride ? this.overrides[plan.id] : undefined,
      );
      const computed = computePlanTotals(
        this.desiredNet,
        plan.mdr,
        share,
        this.grossUp,
      );
      const over =
        on &&
        !!computed.ok &&
        (computed.total ?? 0) > this.availableCredit + 0.005;
      const fee = computed.fee ?? 0;
      return {
        plan,
        on,
        over,
        totalStr: computed.ok
          ? formatPlanAmount(computed.total ?? 0)
          : this.desiredNet > 0 && computed.needsSplit
            ? 'Needs split %'
            : '—',
        feeStr:
          computed.ok && fee > 0.005
            ? `includes ${formatPlanAmount(fee)} fee`
            : '',
        mdrPct: `MDR ${(plan.mdr * 100).toFixed(2)}%`,
        showOverride: on && this.grossUp,
        custom: hasOverride,
        overrideVal: hasOverride ? this.overrides[plan.id] : '',
        overrideLabel: hasOverride ? 'Use default split' : 'Override fee split',
        needsSplit: !!computed.needsSplit,
      };
    });
  }

  get summaryPlans(): Array<{
    label: string;
    totalStr: string;
    feeStr: string;
    splitStr: string;
    settleStr: string;
    custom: boolean;
  }> {
    return this.selectedOffers.map((o) => ({
      label: o.label,
      totalStr: formatPlanAmount(o.total),
      feeStr:
        o.fee > 0.005
          ? `+${formatPlanAmount(o.fee)} buyer-covered fee`
          : 'No buyer fee',
      splitStr:
        o.split === null
          ? 'No gross-up'
          : `Buyer covers ${o.split}% of MDR` + (o.custom ? ' · Custom' : ''),
      settleStr: formatPlanAmount(o.total - o.total * o.mdr),
      custom: o.custom,
    }));
  }

  public toggleGrossUp(): void {
    const ctrl = this.submitForm.get('grossUp');
    ctrl?.setValue(!ctrl.value);
  }

  public onSplitChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.submitForm.get('splitPct')?.setValue(clampPercentInput(input.value));
  }

  public togglePlan(planId: number): void {
    if (this.selectedPlanIds.has(planId)) {
      this.selectedPlanIds.delete(planId);
      delete this.overrides[planId];
    } else {
      this.selectedPlanIds.add(planId);
    }
  }

  public toggleOverride(planId: number, event: Event): void {
    event.stopPropagation();
    if (this.overrides[planId] !== undefined) {
      delete this.overrides[planId];
    } else {
      this.overrides[planId] = this.splitPct || '';
    }
  }

  public onOverrideChange(planId: number, event: Event): void {
    event.stopPropagation();
    const input = event.target as HTMLInputElement;
    this.overrides[planId] = clampPercentInput(input.value);
  }

  public stopProp(event: Event): void {
    event.stopPropagation();
  }

  public openReview(): void {
    if (!this.canSubmit) {
      return;
    }
    this.overlayStep = 'review';
  }

  public closeReview(): void {
    this.overlayStep = 'form';
  }

  public backFromError(): void {
    this.overlayStep = 'review';
  }

  public onFileChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    if (target.files) {
      const newFiles = Array.from(target.files);
      this.uploadedFiles = [...this.uploadedFiles, ...newFiles];
      this.submitForm.get('invoice')?.setValue(this.uploadedFiles);
    }
  }

  public removeFile(index: number): void {
    this.uploadedFiles.splice(index, 1);
    this.submitForm
      .get('invoice')
      ?.setValue(this.uploadedFiles.length > 0 ? this.uploadedFiles : null);
  }

  private invoiceValidator(): ValidatorFn {
    const allowedFormats = ['application/pdf', 'image/jpeg', 'image/png'];
    const maxSize = 5 * 1024 * 1024;

    return (
      control: AbstractControl<File[] | null>,
    ): ValidationErrors | null => {
      if (!control.value || control.value.length === 0) {
        return null;
      }
      const files = control.value;
      const errors: ValidationErrors = {};
      for (const file of files) {
        if (!allowedFormats.includes(file.type)) {
          errors['format'] = true;
          break;
        }
        if (file.size > maxSize) {
          errors['size'] = true;
          break;
        }
      }
      return Object.keys(errors).length > 0 ? errors : null;
    };
  }

  public async sendOffer(): Promise<void> {
    if (!this.canSubmit || this.isSubmitting) {
      return;
    }

    this.isSubmitting = true;

    try {
      const fileIds = await this.uploadFiles(this.uploadedFiles);
      const offers = this.selectedOffers;
      const planPayload: CreateTransactionRequestPlanOffer[] = offers.map(
        (o) => ({
          planId: o.planId,
          splitPercentage: o.split === null ? 0 : o.split,
        }),
      );

      const request: CreateTransactionRequestData = {
        amount: this.desiredNet,
        buyerId: this.data.customer.id,
        memo: this.submitForm.get('memo')?.value || '',
        plans: planPayload,
        poNumber: this.submitForm.get('ponumber')?.value || '',
        invoiceNumber: this.submitForm.get('inumber')?.value || '',
        fileIds,
      };

      this._transactionRequestService
        .createRequest$(request)
        .pipe(
          finalize(() => setTimeout(() => (this.isSubmitting = false), 400)),
        )
        .subscribe({
          next: () => {
            this.overlayStep = 'sent';
          },
          error: (err) => {
            console.log(
              '[BUILD-OFFER-DIALOG-COMPONENT]: could not submit request',
              err,
            );
            this.errorTime = new Date().toLocaleTimeString('en-US', {
              hour: 'numeric',
              minute: '2-digit',
              second: '2-digit',
            });
            this.overlayStep = 'error';
          },
        });
    } catch (err) {
      console.error(
        '[BUILD-OFFER-DIALOG-COMPONENT]: Error submitting request:',
        err,
      );
      this.errorTime = new Date().toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        second: '2-digit',
      });
      this.overlayStep = 'error';
      this.isSubmitting = false;
    }
  }

  public async uploadFiles(files: File[]): Promise<string[]> {
    if (files.length === 0) {
      return [];
    }
    try {
      const uploadPromises = files.map((file) =>
        firstValueFrom(this._fileService.uploadInvoice(file)),
      );
      const fileIds = await Promise.all(uploadPromises);
      return fileIds.filter((id) => id !== null) as string[];
    } catch (err) {
      console.error(
        '[BUILD-OFFER-DIALOG-COMPONENT]: Error uploading files:',
        err,
      );
      throw new Error('File upload failed');
    }
  }

  public close(success: boolean = false): void {
    this._dialogRef.close({ success });
  }

  public done(): void {
    this.close(true);
  }
}
